import { useEffect, useState } from 'react';
import {
  Save, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown, X,
  PanelBottom, Link as LinkIcon, Share2,
  Facebook, Instagram, Twitter, Youtube, Linkedin, Music2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardHeader, CardTitle, CardContent, Input, useConfirm } from '@nepal-ecom/ui';
import { PageHeader } from '@/components/shared/PageHeader';
import { TextField, TextArea } from '@/components/shared/CmsFields';
import { STOREFRONT_URL, cmsUrl, cmsFetch } from '@/lib/cms';

const CMS_URL = cmsUrl('footer');

interface FooterLink { label: string; href: string; badge?: string }
interface FooterColumn { heading: string; links: FooterLink[] }
type SocialIcon = 'facebook' | 'instagram' | 'twitter' | 'youtube' | 'linkedin' | 'tiktok';
interface FooterSocial { icon: SocialIcon; href: string; label: string }
interface FooterContact { address: string; phone: string; email: string }
// Newsletter and paymentMethods used to live here but were removed from the
// storefront footer. The CMS shape stays loose — any leftover keys in
// .cms-footer.json are preserved on save but no longer editable.
interface FooterContent {
  brand: { tagline: string };
  contact: FooterContact;
  columns: FooterColumn[];
  social: FooterSocial[];
  legalName: string;
}

const SOCIAL_OPTIONS: { value: SocialIcon; Icon: React.ElementType; label: string }[] = [
  { value: 'facebook',  Icon: Facebook,  label: 'Facebook' },
  { value: 'instagram', Icon: Instagram, label: 'Instagram' },
  { value: 'twitter',   Icon: Twitter,   label: 'Twitter / X' },
  { value: 'youtube',   Icon: Youtube,   label: 'YouTube' },
  { value: 'linkedin',  Icon: Linkedin,  label: 'LinkedIn' },
  { value: 'tiktok',    Icon: Music2,    label: 'TikTok' },
];

const EMPTY_FOOTER: FooterContent = {
  brand: { tagline: '' },
  contact: { address: '', phone: '', email: '' },
  columns: [],
  social: [],
  legalName: '',
};

export function FooterCms() {
  const confirm = useConfirm();
  const [data, setData] = useState<FooterContent>(EMPTY_FOOTER);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await cmsFetch(CMS_URL, { cache: 'no-store' });
        if (res.ok) setData(await res.json());
      } catch {
        toast.error('Could not reach storefront CMS.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await cmsFetch(CMS_URL, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Footer saved', { description: 'Refresh the storefront to see changes.' });
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  // ---- mutators ----
  const setBrand = (tagline: string) => setData((d) => ({ ...d, brand: { tagline } }));
  const setContact = (patch: Partial<FooterContact>) => setData((d) => ({ ...d, contact: { ...d.contact, ...patch } }));
  const setLegalName = (legalName: string) => setData((d) => ({ ...d, legalName }));

  const updateColumn = (i: number, patch: Partial<FooterColumn>) =>
    setData((d) => ({ ...d, columns: d.columns.map((c, idx) => idx === i ? { ...c, ...patch } : c) }));
  const removeColumn = async (i: number) => {
    if (!(await confirm({
      title: 'Remove this column?',
      description: 'The column and all of its links will be removed.',
      confirmLabel: 'Remove',
      tone: 'danger',
    }))) return;
    setData((d) => ({ ...d, columns: d.columns.filter((_, idx) => idx !== i) }));
  };
  const addColumn = () =>
    setData((d) => ({ ...d, columns: [...d.columns, { heading: 'New column', links: [] }] }));
  const moveColumn = (from: number, to: number) =>
    setData((d) => {
      if (to < 0 || to >= d.columns.length) return d;
      const cols = [...d.columns];
      const [item] = cols.splice(from, 1);
      cols.splice(to, 0, item);
      return { ...d, columns: cols };
    });

  const updateLink = (ci: number, li: number, patch: Partial<FooterLink>) =>
    setData((d) => ({
      ...d,
      columns: d.columns.map((c, idx) => idx === ci
        ? { ...c, links: c.links.map((l, j) => j === li ? { ...l, ...patch } : l) }
        : c),
    }));
  const removeLink = (ci: number, li: number) =>
    setData((d) => ({
      ...d,
      columns: d.columns.map((c, idx) => idx === ci ? { ...c, links: c.links.filter((_, j) => j !== li) } : c),
    }));
  const addLink = (ci: number) =>
    setData((d) => ({
      ...d,
      columns: d.columns.map((c, idx) => idx === ci ? { ...c, links: [...c.links, { label: '', href: '' }] } : c),
    }));

  const updateSocial = (i: number, patch: Partial<FooterSocial>) =>
    setData((d) => ({ ...d, social: d.social.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const removeSocial = (i: number) =>
    setData((d) => ({ ...d, social: d.social.filter((_, idx) => idx !== i) }));
  const addSocial = () =>
    setData((d) => ({ ...d, social: [...d.social, { icon: 'facebook', href: '', label: 'Facebook' }] }));

  return (
    <div className="animate-fade-in space-y-5">
      <PageHeader
        title="Footer CMS"
        description="Edit the brand tagline, contact info, link columns, social icons, and legal name shown in the site footer."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={STOREFRONT_URL} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /> Open storefront</a>
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Publish changes'}
            </Button>
          </div>
        }
      />

      {loading ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-500">Loading…</CardContent></Card>
      ) : (
        <>
          {/* Brand + Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <PanelBottom className="h-4 w-4 text-primary-600" /> Brand & contact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <TextArea label="Brand tagline" value={data.brand.tagline} onChange={setBrand} rows={3} />
              <div className="grid sm:grid-cols-2 gap-3">
                <TextField label="Legal name" value={data.legalName} onChange={setLegalName} placeholder="Shishyastra Pvt. Ltd." />
                <TextField label="Address" value={data.contact.address} onChange={(v) => setContact({ address: v })} placeholder="Sankhamul Marg, Lalitpur…" />
                <TextField label="Phone" value={data.contact.phone} onChange={(v) => setContact({ phone: v })} placeholder="+977 1 4567 890" />
                <TextField label="Email" value={data.contact.email} onChange={(v) => setContact({ email: v })} placeholder="hello@shishyastra.com" />
              </div>
            </CardContent>
          </Card>

          {/* Columns */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <LinkIcon className="h-4 w-4 text-primary-600" />
                Link columns
                <span className="text-xs font-normal text-slate-500">({data.columns.length})</span>
              </CardTitle>
              <Button size="sm" onClick={addColumn}><Plus className="h-4 w-4" /> Add column</Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.columns.length === 0 && (
                <p className="py-6 text-center text-sm text-slate-500">No columns yet.</p>
              )}
              {data.columns.map((col, ci) => (
                <div key={ci} className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
                  <div className="flex items-center gap-2">
                    <Input value={col.heading} onChange={(e) => updateColumn(ci, { heading: e.target.value })}
                      placeholder="Column heading" className="h-9 font-semibold flex-1" />
                    <button type="button" onClick={() => moveColumn(ci, ci - 1)} disabled={ci === 0}
                      className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded disabled:opacity-30">
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => moveColumn(ci, ci + 1)} disabled={ci === data.columns.length - 1}
                      className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded disabled:opacity-30">
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => removeColumn(ci)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-1.5 pl-1">
                    {col.links.map((link, li) => (
                      <div key={li} className="flex items-center gap-2">
                        <Input value={link.label} onChange={(e) => updateLink(ci, li, { label: e.target.value })}
                          placeholder="Label" className="h-8 text-sm w-40 flex-shrink-0" />
                        <Input value={link.href} onChange={(e) => updateLink(ci, li, { href: e.target.value })}
                          placeholder="/path or https://…" className="h-8 text-sm font-mono flex-1 min-w-0" />
                        <Input value={link.badge ?? ''} onChange={(e) => updateLink(ci, li, { badge: e.target.value || undefined })}
                          placeholder="Badge" className="h-8 text-sm w-20 flex-shrink-0" />
                        <button type="button" onClick={() => removeLink(ci, li)}
                          className="p-1 text-slate-400 hover:text-red-500 rounded flex-shrink-0">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => addLink(ci)} className="h-7 text-xs">
                      <Plus className="h-3 w-3" /> Add link
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Social */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Share2 className="h-4 w-4 text-primary-600" />
                Social links
                <span className="text-xs font-normal text-slate-500">({data.social.length})</span>
              </CardTitle>
              <Button size="sm" onClick={addSocial}><Plus className="h-4 w-4" /> Add</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.social.map((s, i) => {
                const Icon = SOCIAL_OPTIONS.find((o) => o.value === s.icon)?.Icon ?? Facebook;
                return (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-white">
                    <select value={s.icon} onChange={(e) => updateSocial(i, { icon: e.target.value as SocialIcon })}
                      className="h-8 px-2 rounded-md border border-slate-200 bg-white text-sm">
                      {SOCIAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <Icon className="h-4 w-4 text-slate-500 flex-shrink-0" />
                    <Input value={s.label} onChange={(e) => updateSocial(i, { label: e.target.value })}
                      placeholder="Label / aria-label" className="h-8 text-sm w-36" />
                    <Input value={s.href} onChange={(e) => updateSocial(i, { href: e.target.value })}
                      placeholder="https://…" className="h-8 text-sm font-mono flex-1" />
                    <button type="button" onClick={() => removeSocial(i)}
                      className="p-1 text-slate-400 hover:text-red-500 rounded">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
              {data.social.length === 0 && (
                <p className="py-4 text-center text-sm text-slate-500">No social links.</p>
              )}
            </CardContent>
          </Card>

        </>
      )}
    </div>
  );
}
